if (!customElements.get('eyf-purchase-options')) {
  customElements.define(
    'eyf-purchase-options',
    class EyfPurchaseOptions extends HTMLElement {
      connectedCallback() {
        if (this.dataset.eyfPoBound === 'true') return;
        this.dataset.eyfPoBound = 'true';

        this.unitPrice = Number(this.dataset.unitPrice || 0);
        this.maxQty = Math.max(1, Number(this.dataset.maxQty || 999));
        this.variantId = Number(this.dataset.variantId || 0);
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.currentQty = 0;
        this.selectedItem = null;
        this.currentDiscount = 0;
        this.isAddingToCart = false;
        this.upsellQty = 0;
        this.upsellVariantId = 0;
        this.upsellMaxQty = 0;
        this.selectedSellingPlanId = 0;
        this.selectedSellingPlanName = '';
        this.selectedSellingPlanButton = null;

        this.initQuantity();
        this.initPurchaseType();
        this.initSimpleForm();
        this.applyDefaultQuantitySelection();
        this.initGallery();
        this.initResultsCarousel();
        this.initAccordions();
        this.initGifts();
        this.initUpsell();
        this.initRatingAnchorScroll();
      }

      initQuantity() {
        this.qtyItems = Array.from(this.querySelectorAll('[data-eyf-po-qty-item]')).map((item) => {
          const minQty = Math.max(1, Number(item.dataset.minQty || 1));
          const discount = Math.min(100, Math.max(0, Number(item.dataset.discount || 0)));
          const showQtySelector = item.dataset.showQtySelector === 'true';
          return { el: item, minQty, discount, showQtySelector };
        });

        this.qtyItems.forEach(({ el, minQty }) => {
          const button = el.querySelector('[data-eyf-po-qty]');
          if (button) {
            button.addEventListener('click', () => this.selectQuantityItem(el, minQty));
          }

          const minus = el.querySelector('[data-eyf-po-stepper-minus]');
          const plus = el.querySelector('[data-eyf-po-stepper-plus]');
          if (minus) {
            minus.addEventListener('click', (event) => {
              event.stopPropagation();
              this.changeQty(-1);
            });
          }
          if (plus) {
            plus.addEventListener('click', (event) => {
              event.stopPropagation();
              this.changeQty(1);
            });
          }
        });

        const firstBtn = this.querySelector('[data-eyf-po-qty]');
        if (firstBtn) firstBtn.tabIndex = 0;
      }

      applyDefaultQuantitySelection() {
        if (this.dataset.preselectFirstQty !== 'true') return;
        if (!Array.isArray(this.qtyItems) || this.qtyItems.length === 0) return;

        const first = this.qtyItems[0];
        this.selectQuantityItem(first.el, first.minQty);
      }

      selectQuantityItem(itemEl, qty) {
        const nextQty = Math.min(this.maxQty, Math.max(1, Number(qty) || 1));
        this.applyQuantitySelection(itemEl, nextQty);
      }

      changeQty(delta) {
        if (!this.selectedItem) return;

        const tier = this.qtyItems.find((item) => item.el === this.selectedItem);
        if (!tier || !tier.showQtySelector) return;

        const nextQty = Math.min(this.maxQty, Math.max(tier.minQty, this.currentQty + delta));
        if (nextQty === this.currentQty) return;

        this.applyQuantitySelection(tier.el, nextQty);
      }

      applyQuantitySelection(itemEl, qty) {
        const tier = this.qtyItems.find((item) => item.el === itemEl);
        if (!tier) return;

        const resolvedQty = Math.min(this.maxQty, Math.max(tier.minQty, qty));

        this.currentQty = resolvedQty;
        this.selectedItem = itemEl;
        this.currentDiscount = tier.discount;

        this.qtyItems.forEach(({ el, minQty, discount }) => {
          const isActive = el === itemEl;
          el.classList.toggle('is-selected', isActive);

          const btn = el.querySelector('[data-eyf-po-qty]');
          if (btn) {
            btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
            btn.tabIndex = isActive ? 0 : -1;
          }

          const stepper = el.querySelector('[data-eyf-po-stepper]');
          if (stepper) {
            if (isActive) stepper.removeAttribute('hidden');
            else stepper.setAttribute('hidden', '');
          }

          if (!isActive) {
            this.updateSelectedPrice(el, minQty, discount);
            this.updateStepperUI(el, minQty);
          }
        });

        this.updateStepperUI(itemEl, resolvedQty);
        this.updateSelectedPrice(itemEl, resolvedQty, tier.discount);
        this.updateStepperButtons(itemEl, resolvedQty, tier.minQty);
        this.updateOnetimeAtcState();
        this.updateSubscribeState();
        this.refreshSimpleFormUI();

        const button = itemEl.querySelector('[data-eyf-po-qty]');
        this.dispatchEvent(
          new CustomEvent('eyf-po:quantity-change', {
            bubbles: true,
            detail: {
              quantity: resolvedQty,
              blockId: button?.dataset.blockId || '',
              discount: tier.discount,
            },
          })
        );
      }

      updateStepperUI(itemEl, qty) {
        const valueEl = itemEl.querySelector('[data-eyf-po-stepper-value]');
        if (valueEl) valueEl.textContent = String(qty);
      }

      updateStepperButtons(itemEl, qty, minQty) {
        const minus = itemEl.querySelector('[data-eyf-po-stepper-minus]');
        const plus = itemEl.querySelector('[data-eyf-po-stepper-plus]');

        if (minus) minus.disabled = qty <= minQty;
        if (plus) plus.disabled = qty >= this.maxQty;
      }

      updateSelectedPrice(itemEl, qty, discount) {
        const salePrice = this.calcPrice(qty, discount);
        const priceEl = itemEl.querySelector('[data-eyf-po-qty-price-amount]');
        if (priceEl) {
          priceEl.textContent = this.formatMoney(salePrice);
        }

        const compareEl = itemEl.querySelector('[data-eyf-po-qty-compare]');
        if (compareEl) {
          const comparePrice = this.calcComparePrice(qty);
          compareEl.textContent = this.formatMoney(comparePrice);
          if (comparePrice > salePrice) {
            compareEl.removeAttribute('hidden');
          } else {
            compareEl.setAttribute('hidden', '');
          }
        }
      }

      calcPrice(qty, discount) {
        const subtotal = this.unitPrice * qty;
        const multiplier = (100 - Number(discount || 0)) / 100;
        return Math.round(subtotal * multiplier);
      }

      calcComparePrice(qty) {
        return Math.round(this.unitPrice * qty);
      }

      formatMoney(cents) {
        if (typeof cents !== 'number' || Number.isNaN(cents)) cents = 0;

        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, this.moneyFormat);
        }

        const abs = Math.abs(cents);
        const dollars = Math.floor(abs / 100);
        const remainder = String(abs % 100).padStart(2, '0');
        const amount = `${dollars}.${remainder}`;
        const amountNoDecimals = String(dollars);
        const amountWithComma = amount.replace('.', ',');

        let formatted = this.moneyFormat
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
          .replace(/\{\{\s*amount\s*\}\}/g, amount);

        if (cents < 0) formatted = `-${formatted}`;
        return formatted;
      }

      initPurchaseType() {
        this.typePanel = this.querySelector('[data-eyf-po-type-panel]');
        if (!this.typePanel) return;
        this.typeError = this.querySelector('[data-eyf-po-type-error]');
        this.onetimeItem = this.querySelector('[data-eyf-po-onetime]');
        this.onetimeLabel = this.querySelector('[data-eyf-po-onetime-label]');
        this.onetimePrice = this.querySelector('[data-eyf-po-onetime-price]');
        this.subscribeItem = this.querySelector('[data-eyf-po-subscribe-item]');
        this.subscribeCard = this.querySelector('[data-eyf-po-subscribe-card]');
        this.subscribeToggle = this.querySelector('[data-eyf-po-subscribe-toggle]');
        this.subscribePlans = this.querySelector('[data-eyf-po-subscribe-plans]');
        this.subscribeSubtitle = this.querySelector('[data-eyf-po-subscribe-subtitle]');
        this.subscribeCompare = this.querySelector('[data-eyf-po-subscribe-compare]');
        this.subscribePrice = this.querySelector('[data-eyf-po-subscribe-price]');
        this.sellingPlanButtons = Array.from(this.querySelectorAll('[data-eyf-po-selling-plan]'));

        if (this.typePanel) {
          this.typePanel.addEventListener('click', (event) => {
            if (this.hasQtySelected()) return;
            event.preventDefault();
            event.stopPropagation();
            this.showTypeError();
          });
        }

        this.querySelectorAll('[data-eyf-po-type]').forEach((button) => {
          button.addEventListener('click', () => this.selectPurchaseType(button));
        });

        this.sellingPlanButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.stopPropagation();
            this.selectSellingPlan(button);
          });
        });

        this.updateTypePanelLock();
      }

      initSimpleForm() {
        this.simpleForm = this.querySelector('[data-eyf-po-simple-form]');
        if (!this.simpleForm) return;

        this.simpleFormMode = true;
        this.hideSimpleQty = this.dataset.hideSimpleQty === 'true';
        this.qtyOptionsDriveSimpleForm =
          this.hideSimpleQty && Array.isArray(this.qtyItems) && this.qtyItems.length > 0;

        // When qty options drive ATC, wait for a selection instead of defaulting to 1.
        this.currentQty = this.qtyOptionsDriveSimpleForm ? 0 : 1;
        this.currentDiscount = 0;

        const minus = this.simpleForm.querySelector('[data-eyf-po-simple-minus]');
        const plus = this.simpleForm.querySelector('[data-eyf-po-simple-plus]');
        const valueEl = this.simpleForm.querySelector('[data-eyf-po-simple-value]');
        const atcBtn = this.simpleForm.querySelector('[data-eyf-po-simple-atc]');

        if (minus) {
          minus.addEventListener('click', () => this.changeSimpleQty(-1));
        }
        if (plus) {
          plus.addEventListener('click', () => this.changeSimpleQty(1));
        }
        if (atcBtn) {
          atcBtn.addEventListener('click', () => {
            if (this.qtyOptionsDriveSimpleForm && !this.hasQtySelected()) return;
            this.addToCart(atcBtn);
          });
        }

        this.refreshSimpleFormUI();
      }

      changeSimpleQty(delta) {
        if (!this.simpleFormMode || this.hideSimpleQty) return;

        const nextQty = Math.min(this.maxQty, Math.max(1, this.currentQty + delta));
        if (nextQty === this.currentQty) return;

        this.currentQty = nextQty;
        this.currentDiscount = 0;
        this.selectedItem = null;
        this.qtyItems?.forEach(({ el, minQty, discount }, index) => {
          el.classList.remove('is-selected');
          const btn = el.querySelector('[data-eyf-po-qty]');
          if (btn) {
            btn.setAttribute('aria-checked', 'false');
            btn.tabIndex = index === 0 ? 0 : -1;
          }
          const stepper = el.querySelector('[data-eyf-po-stepper]');
          if (stepper) stepper.setAttribute('hidden', '');
          this.updateSelectedPrice(el, minQty, discount);
          this.updateStepperUI(el, minQty);
        });
        this.refreshSimpleFormUI();
      }

      refreshSimpleFormUI() {
        if (!this.simpleForm) return;
        this.updateSimpleFormUI(
          this.simpleForm.querySelector('[data-eyf-po-simple-minus]'),
          this.simpleForm.querySelector('[data-eyf-po-simple-plus]'),
          this.simpleForm.querySelector('[data-eyf-po-simple-value]'),
          this.simpleForm.querySelector('[data-eyf-po-simple-atc]')
        );
      }

      updateSimpleFormUI(minus, plus, valueEl, atcBtn) {
        const displayQty = Math.max(1, this.currentQty || 1);
        const discount = this.currentDiscount || 0;

        if (valueEl) valueEl.textContent = String(displayQty);
        if (minus) minus.disabled = displayQty <= 1;
        if (plus) plus.disabled = displayQty >= this.maxQty;
        if (atcBtn) {
          const priceEl = atcBtn.querySelector('[data-eyf-po-simple-price]');
          if (priceEl) {
            priceEl.textContent = this.formatMoney(
              this.calcPrice(
                this.qtyOptionsDriveSimpleForm && !this.hasQtySelected() ? 1 : displayQty,
                this.qtyOptionsDriveSimpleForm && !this.hasQtySelected() ? 0 : discount
              )
            );
          }
          const needsQtySelection = this.qtyOptionsDriveSimpleForm && !this.hasQtySelected();
          atcBtn.disabled = !this.variantId || this.currentQty < 1 || needsQtySelection;
          if (this.isAddingToCart) atcBtn.setAttribute('aria-busy', 'true');
          else atcBtn.removeAttribute('aria-busy');
        }
      }

      hasQtySelected() {
        return Boolean(this.selectedItem && this.currentQty > 0);
      }

      showTypeError() {
        if (!this.typeError) return;
        this.typeError.hidden = false;
        if (this.typePanel) this.typePanel.classList.add('has-error');
      }

      hideTypeError() {
        if (!this.typeError) return;
        this.typeError.hidden = true;
        if (this.typePanel) this.typePanel.classList.remove('has-error');
      }

      updateTypePanelLock() {
        if (!this.typePanel) return;

        const locked = !this.hasQtySelected();
        this.typePanel.classList.toggle('is-locked', locked);
        this.typePanel.setAttribute('aria-disabled', locked ? 'true' : 'false');

        this.querySelectorAll('[data-eyf-po-type]').forEach((button) => {
          if (locked) {
            button.setAttribute('disabled', '');
            return;
          }

          const noSellingPlans =
            button.dataset.purchaseType === 'subscribe' && this.sellingPlanButtons.length === 0;
          if (noSellingPlans) button.setAttribute('disabled', '');
          else button.removeAttribute('disabled');
        });

        if (!locked) this.hideTypeError();
      }

      updateOnetimeAtcState() {
        if (!this.onetimeItem) return;

        const isReady = this.hasQtySelected();
        this.onetimeItem.classList.toggle('is-atc', isReady);

        if (isReady) {
          this.onetimeItem.classList.remove('is-selected');
          const onetimeBtn = this.onetimeItem.querySelector('[data-eyf-po-type]');
          if (onetimeBtn) onetimeBtn.setAttribute('aria-checked', 'false');
        }

        if (this.onetimeLabel) {
          const defaultLabel = this.onetimeLabel.dataset.labelDefault || '';
          const atcLabel = this.onetimeLabel.dataset.labelAtc || 'Add to Bag';
          this.onetimeLabel.textContent = isReady ? atcLabel : defaultLabel;
        }

        if (this.onetimePrice) {
          if (isReady) {
            this.onetimePrice.textContent = this.formatMoney(
              this.calcPrice(this.currentQty, this.currentDiscount)
            );
          } else {
            this.onetimePrice.textContent = this.onetimePrice.dataset.priceDefault || '';
          }
        }

        this.updateTypePanelLock();
      }

      updateSubscribeState() {
        if (!this.subscribeItem) return;

        const isReady = this.hasQtySelected();
        this.subscribeItem.classList.toggle('is-ready', isReady);

        if (!isReady) {
          this.closeSubscribeAccordion();
          this.clearSellingPlanSelection();
        }

        this.updateSubscribeHeaderPrices();
        this.updateSubscribeSubtitle();
      }

      updateSubscribeHeaderPrices() {
        if (!this.subscribeCompare || !this.subscribePrice) return;

        const planButton = this.getActiveSellingPlanButton();
        const isReady = this.hasQtySelected();
        const isOpen = this.subscribeCard?.classList.contains('is-open');

        if (!isReady || !isOpen || !planButton) {
          this.subscribeCompare.textContent = this.subscribeCompare.dataset.priceDefault || '';
          this.subscribePrice.textContent = this.subscribePrice.dataset.priceDefault || '';
          return;
        }

        const prices = this.getSellingPlanPrices(planButton);
        this.subscribeCompare.textContent = this.formatMoney(prices.compare);
        this.subscribePrice.textContent = this.formatMoney(prices.price);
      }

      formatSellingPlanSubtitle(planName) {
        const trimmed = String(planName || '').trim();
        if (!trimmed) return '';

        const period = trimmed.split(',')[0].trim();
        if (!period) return trimmed;

        return `${period} subscription`;
      }

      updateSubscribeSubtitle() {
        if (!this.subscribeSubtitle) return;

        const emptyText = this.subscribeSubtitle.dataset.subtitleEmpty || 'Nothing selected';
        const prefix = this.subscribeSubtitle.dataset.subtitlePrefix || 'Selected:';

        if (!this.selectedSellingPlanName) {
          this.subscribeSubtitle.textContent = emptyText;
          return;
        }

        const subtitleText = this.formatSellingPlanSubtitle(this.selectedSellingPlanName);

        this.subscribeSubtitle.innerHTML = `<strong>${prefix}</strong> ${this.escapeHtml(subtitleText)}`;
      }

      getActiveSellingPlanButton() {
        if (this.selectedSellingPlanButton) return this.selectedSellingPlanButton;
        return this.sellingPlanButtons[0] || null;
      }

      getSellingPlanPrices(planButton) {
        const unitPrice = Number(planButton.dataset.planPrice || 0);
        const unitCompare = Number(planButton.dataset.planCompare || unitPrice);
        const multiplier = (100 - Number(this.currentDiscount || 0)) / 100;

        return {
          price: Math.round(unitPrice * this.currentQty * multiplier),
          compare: Math.round(unitCompare * this.currentQty * multiplier),
        };
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      openSubscribeAccordion() {
        if (!this.subscribeCard || !this.subscribePlans || !this.subscribeToggle) return;

        this.subscribeCard.classList.add('is-open');
        this.subscribePlans.removeAttribute('hidden');
        this.subscribeToggle.setAttribute('aria-expanded', 'true');
        this.updateSubscribeHeaderPrices();
        this.setGiftsAvailable(true);
        this.openGifts();
      }

      closeSubscribeAccordion() {
        if (!this.subscribeCard || !this.subscribePlans || !this.subscribeToggle) return;

        this.subscribeCard.classList.remove('is-open');
        this.subscribePlans.setAttribute('hidden', '');
        this.subscribeToggle.setAttribute('aria-expanded', 'false');
        this.updateSubscribeHeaderPrices();
        this.setGiftsAvailable(false);
      }

      clearSellingPlanSelection() {
        this.selectedSellingPlanId = 0;
        this.selectedSellingPlanName = '';
        this.selectedSellingPlanButton = null;

        this.sellingPlanButtons.forEach((button) => {
          button.classList.remove('is-selected');
          button.setAttribute('aria-pressed', 'false');
        });
      }

      selectTypeItem(item) {
        this.querySelectorAll('[data-eyf-po-type-item]').forEach((el) => {
          const isActive = el === item;
          el.classList.toggle('is-selected', isActive);

          const btn = el.querySelector('[data-eyf-po-type]');
          if (btn) {
            btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
            btn.tabIndex = isActive ? 0 : -1;
          }
        });
      }

      selectSellingPlan(button) {
        if (!this.hasQtySelected()) {
          this.showTypeError();
          return;
        }

        const planId = Number(button.dataset.sellingPlanId || 0);
        if (!planId) return;

        this.selectTypeItem(this.subscribeItem);
        this.openSubscribeAccordion();

        this.selectedSellingPlanId = planId;
        this.selectedSellingPlanName = button.dataset.planName || '';
        this.selectedSellingPlanButton = button;

        this.sellingPlanButtons.forEach((planButton) => {
          const isActive = planButton === button;
          planButton.classList.toggle('is-selected', isActive);
          planButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.updateSubscribeHeaderPrices();
        this.updateSubscribeSubtitle();
        this.addToCart(button, planId);
      }

      selectPurchaseType(button) {
        if (!this.hasQtySelected()) {
          this.showTypeError();
          return;
        }

        const item = button.closest('[data-eyf-po-type-item]');
        if (!item) return;

        const purchaseType = button.dataset.purchaseType || '';

        if (purchaseType === 'onetime' && this.onetimeItem?.classList.contains('is-atc')) {
          this.closeSubscribeAccordion();
          this.setGiftsAvailable(false);
          this.selectTypeItem(item);
          this.addToCart(button);
          return;
        }

        if (purchaseType === 'subscribe') {
          const isOpen = this.subscribeCard?.classList.contains('is-open');
          const isSelected = item.classList.contains('is-selected');

          this.selectTypeItem(item);

          if (isSelected && isOpen) {
            this.closeSubscribeAccordion();
          } else {
            this.openSubscribeAccordion();
            this.updateSubscribeHeaderPrices();
          }

          this.dispatchEvent(
            new CustomEvent('eyf-po:purchase-type-change', {
              bubbles: true,
              detail: { purchaseType },
            })
          );
          return;
        }

        if (item.classList.contains('is-selected')) return;

        this.closeSubscribeAccordion();
        this.selectTypeItem(item);

        this.dispatchEvent(
          new CustomEvent('eyf-po:purchase-type-change', {
            bubbles: true,
            detail: { purchaseType },
          })
        );
      }

      normalizeCartAddResponse(response) {
        if (!response?.items || response.key) return response;

        const mainItem =
          response.items.find((item) => Number(item.id) === Number(this.variantId)) || response.items[0];

        if (!mainItem) return response;

        return {
          ...mainItem,
          sections: response.sections,
        };
      }

      addToCart(button, sellingPlanId = 0) {
        if (this.isAddingToCart) return;
        if (!this.variantId || !this.currentQty) return;

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const config = fetchConfig('javascript');
        const mainLine = {
          id: this.variantId,
          quantity: this.currentQty,
        };

        if (sellingPlanId) {
          mainLine.selling_plan = sellingPlanId;
        }

        const items = [mainLine];

        if (this.upsellQty > 0 && this.upsellVariantId) {
          items.push({ id: this.upsellVariantId, quantity: this.upsellQty });
        }

        const body = items.length === 1 ? mainLine : { items };

        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(document.activeElement);
          }
        }

        this.isAddingToCart = true;
        if (button) button.setAttribute('aria-busy', 'true');
        if (this.simpleFormMode) {
          this.refreshSimpleFormUI();
        }

        fetch(`${routes.cart_add_url}`, {
          ...config,
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              console.error(response.description || response.message || 'Add to cart failed');
              return;
            }

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // Multi-item /cart/add.js returns { items: [...], sections } without top-level `key`.
            // cart-notification.renderContents expects a single line item shape ({ key, sections }).
            const cartData = this.normalizeCartAddResponse(response);

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'eyf-purchase-options',
                productVariantId: this.variantId,
                cartData,
              });
            }

            if (typeof cart.renderContents === 'function') {
              cart.renderContents(cartData);
            }

            if (cart.classList.contains('is-empty')) {
              cart.classList.remove('is-empty');
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.isAddingToCart = false;
            if (button) button.removeAttribute('aria-busy');
            if (this.simpleFormMode) {
              this.refreshSimpleFormUI();
            }
          });
      }

      initGallery() {
        if (typeof Swiper === 'undefined') return;

        const mainEl = this.querySelector('[data-eyf-po-gallery-main]');
        if (!mainEl) return;

        const thumbsEl = this.querySelector('[data-eyf-po-thumbs]');
        let thumbsSwiper;

        if (thumbsEl) {
          thumbsSwiper = new Swiper(thumbsEl, {
            spaceBetween: 12,
            slidesPerView: 'auto',
            freeMode: true,
            watchSlidesProgress: true,
            watchOverflow: true,
            breakpoints: {
              0: { spaceBetween: 8 },
              750: { spaceBetween: 16 },
              1200: { spaceBetween: 32 },
            },
          });

          const nextBtn = this.querySelector('[data-eyf-po-thumbs-next]');
          if (nextBtn) {
            nextBtn.addEventListener('click', () => {
              thumbsSwiper.slideNext();
            });
          }

          this.thumbsSwiper = thumbsSwiper;
        }

        const galleryDotsEl = this.querySelector('[data-eyf-po-gallery-dots]');

        this.gallerySwiper = new Swiper(mainEl, {
          spaceBetween: 0,
          watchOverflow: true,
          ...(thumbsSwiper
            ? {
                thumbs: {
                  swiper: thumbsSwiper,
                  slideThumbActiveClass: 'is-active',
                },
              }
            : {}),
          pagination: galleryDotsEl
            ? {
                el: galleryDotsEl,
                clickable: true,
                bulletClass: 'eyf-po__gallery-dot',
                bulletActiveClass: 'is-active',
              }
            : undefined,
        });
      }

      initResultsCarousel() {
        if (typeof Swiper === 'undefined') return;

        const el = this.querySelector('[data-eyf-po-results-swiper]');
        if (!el) return;

        const paginationEl = this.querySelector('[data-eyf-po-results-dots]');
        const prevEl = this.querySelector('[data-eyf-po-results-prev]');
        const nextEl = this.querySelector('[data-eyf-po-results-next]');
        const slideCount = el.querySelectorAll('.swiper-slide').length;

        this.resultsSwiper = new Swiper(el, {
          slidesPerView: 'auto',
          spaceBetween: 4,
          centeredSlides: true,
          initialSlide: slideCount > 1 ? 1 : 0,
          watchOverflow: false,
          allowTouchMove: true,
          simulateTouch: true,
          grabCursor: true,
          resistanceRatio: 0.85,
          mousewheel: {
            forceToAxis: true,
            sensitivity: 1,
            releaseOnEdges: true,
            thresholdDelta: 40,
            thresholdTime: 400,
          },
          breakpoints: {
            0: {
              centeredSlides: false,
              initialSlide: 0,
            },
            750: {
              centeredSlides: true,
              initialSlide: slideCount > 1 ? 1 : 0,
            },
          },
          pagination: paginationEl
            ? {
                el: paginationEl,
                clickable: true,
                bulletClass: 'eyf-po__results-dot',
                bulletActiveClass: 'is-active',
              }
            : undefined,
          navigation:
            prevEl && nextEl
              ? {
                  prevEl,
                  nextEl,
                }
              : undefined,
        });
      }

      initAccordions() {
        this.querySelectorAll('[data-eyf-po-accordion]').forEach((accordion) => {
          const button = accordion.querySelector('[data-eyf-po-accordion-btn]');
          const panel = accordion.querySelector('[data-eyf-po-accordion-panel]');
          if (!button || !panel) return;

          button.addEventListener('click', () => {
            const isOpen = accordion.classList.contains('is-open');
            accordion.classList.toggle('is-open', !isOpen);
            button.setAttribute('aria-expanded', (!isOpen).toString());
            if (isOpen) panel.setAttribute('hidden', '');
            else panel.removeAttribute('hidden');
          });
        });
      }

      initGifts() {
        this.giftsEl = this.querySelector('[data-eyf-po-gifts]');
        this.giftsToggle = this.querySelector('[data-eyf-po-gifts-toggle]');
        this.giftsPanel = this.querySelector('[data-eyf-po-gifts-panel]');
        this.giftsLabel = this.querySelector('[data-eyf-po-gifts-label]');
        this.giftsAvailable = false;

        if (!this.giftsEl || !this.giftsToggle) return;

        this.setGiftsAvailable(false);

        if (!this.isGiftsInteractive() || !this.giftsPanel) return;

        this.giftsToggle.addEventListener('click', () => {
          if (!this.giftsAvailable) return;
          if (this.giftsEl.classList.contains('is-open')) this.closeGifts();
          else this.openGifts();
        });
      }

      isGiftsInteractive() {
        return this.giftsEl?.dataset.giftsInteractive === 'true';
      }

      setGiftsAvailable(available) {
        if (!this.giftsEl || !this.giftsToggle) return;

        const nextAvailable = Boolean(available) && this.isGiftsInteractive();
        this.giftsAvailable = nextAvailable;
        this.giftsEl.classList.toggle('is-disabled', !nextAvailable);

        if (nextAvailable) {
          this.giftsToggle.removeAttribute('disabled');
          this.updateGiftsLabel(true);
          return;
        }

        this.giftsToggle.setAttribute('disabled', '');
        this.closeGifts({ resetCount: true });
      }

      updateGiftsLabel(showRealCount) {
        if (!this.giftsLabel || !this.giftsEl) return;

        const label = this.giftsLabel.dataset.label || this.giftsLabel.textContent.replace(/\s*\(\d+\)\s*$/, '');
        const count = showRealCount && this.giftsAvailable ? Number(this.giftsEl.dataset.giftsCount || 0) : 0;
        this.giftsLabel.textContent = `${label} (${count})`;
      }

      openGifts() {
        if (!this.giftsAvailable || !this.giftsEl || !this.giftsToggle || !this.giftsPanel) return;

        this.giftsEl.classList.add('is-open');
        this.giftsPanel.removeAttribute('hidden');
        this.giftsToggle.setAttribute('aria-expanded', 'true');
        this.updateGiftsLabel(true);
      }

      closeGifts({ resetCount = false } = {}) {
        if (!this.giftsEl || !this.giftsToggle || !this.giftsPanel) return;

        this.giftsEl.classList.remove('is-open');
        this.giftsPanel.setAttribute('hidden', '');
        this.giftsToggle.setAttribute('aria-expanded', 'false');
        this.updateGiftsLabel(resetCount ? false : this.giftsAvailable);
      }

      initUpsell() {
        const upsell = this.querySelector('[data-eyf-po-upsell]');
        if (!upsell) return;

        this.upsellVariantId = Number(upsell.dataset.upsellVariantId || 0);
        this.upsellMaxQty = Math.max(0, Number(upsell.dataset.upsellMaxQty || 0));
        this.upsellQty = 0;

        const minus = upsell.querySelector('[data-eyf-po-upsell-minus]');
        const plus = upsell.querySelector('[data-eyf-po-upsell-plus]');
        const valueEl = upsell.querySelector('[data-eyf-po-upsell-qty]');

        if (minus) {
          minus.addEventListener('click', () => this.changeUpsellQty(-1));
        }
        if (plus) {
          plus.addEventListener('click', () => this.changeUpsellQty(1));
        }

        this.updateUpsellUI(minus, plus, valueEl);
      }

      changeUpsellQty(delta) {
        const nextQty = Math.min(this.upsellMaxQty, Math.max(0, this.upsellQty + delta));
        if (nextQty === this.upsellQty) return;

        this.upsellQty = nextQty;

        const upsell = this.querySelector('[data-eyf-po-upsell]');
        if (!upsell) return;

        this.updateUpsellUI(
          upsell.querySelector('[data-eyf-po-upsell-minus]'),
          upsell.querySelector('[data-eyf-po-upsell-plus]'),
          upsell.querySelector('[data-eyf-po-upsell-qty]')
        );

        this.dispatchEvent(
          new CustomEvent('eyf-po:upsell-change', {
            bubbles: true,
            detail: {
              quantity: this.upsellQty,
              variantId: this.upsellVariantId,
            },
          })
        );
      }

      updateUpsellUI(minus, plus, valueEl) {
        if (valueEl) valueEl.textContent = String(this.upsellQty);
        if (minus) minus.disabled = this.upsellQty <= 0;
        if (plus) plus.disabled = this.upsellQty >= this.upsellMaxQty;
      }

      initRatingAnchorScroll() {
        const ratingEl = this.querySelector('[data-eyf-po-rating-scroll-to]');
        if (!ratingEl) return;

        const scrollTo = (ratingEl.dataset.eyfPoRatingScrollTo || '').trim();
        if (!scrollTo) return;

        const resolveTarget = (selector) => {
          if (!selector) return null;

          if (selector.startsWith('#')) {
            const id = CSS.escape(selector.slice(1));
            return document.getElementById(selector.slice(1)) || document.querySelector(`#${id}`);
          }

          if (selector.startsWith('.')) {
            try {
              return document.querySelector(selector);
            } catch (e) {
              return null;
            }
          }

          return (
            document.getElementById(selector) ||
            document.querySelector(`#${CSS.escape(selector)}`) ||
            document.querySelector(selector)
          );
        };

        const onActivate = (event) => {
          event.preventDefault();
          event.stopPropagation();

          const target = resolveTarget(scrollTo);
          if (!target) return;

          const headerOffset = Number.parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue('--header-height')
          ) || 0;
          const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 16;

          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

          if (scrollTo.startsWith('#')) {
            try {
              if (window.location.hash !== scrollTo) {
                history.replaceState(null, '', scrollTo);
              }
            } catch (e) {
              // Ignore history errors in edge environments.
            }
          }
        };

        ratingEl.addEventListener('click', onActivate);
        ratingEl.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          onActivate(event);
        });
      }

      disconnectedCallback() {
        if (this.gallerySwiper) {
          this.gallerySwiper.destroy(true, true);
          this.gallerySwiper = null;
        }
        if (this.thumbsSwiper) {
          this.thumbsSwiper.destroy(true, true);
          this.thumbsSwiper = null;
        }
        if (this.resultsSwiper) {
          this.resultsSwiper.destroy(true, true);
          this.resultsSwiper = null;
        }
      }
    }
  );
}
